// One form's whole life: skeleton, config, render, validate, submit, done.

import { fetchConfig, submit } from "./api"
import { renderForm, renderSkeleton, renderConfirmation } from "./render"
import { installStyles } from "./styles"
import { validate } from "./validate"
import { clearErrors, showError } from "./status"

export class Widget {
  constructor(element, token, origin) {
    this.element = element
    this.token = token
    this.origin = origin || window.location.origin
    this.bare = element.getAttribute("data-mk-style") === "bare"
    this.headingLevel = clampHeadingLevel(element.getAttribute("data-mk-heading-level"))
    this.sending = false

    // Kept so a failed config fetch can put back exactly what the customer
    // pasted -- which is a working link to the hosted page.
    this.fallback = Array.prototype.slice.call(element.childNodes)
  }

  start() {
    if (!this.bare) installStyles()

    // Guarded, because classList.add("") throws InvalidCharacterError -- which
    // in bare mode would kill the widget here, before it rendered anything at
    // all, on exactly the sites that asked for the least interference.
    if (!this.bare) this.element.classList.add("mk-signup")
    this.element.setAttribute("aria-busy", "true")
    this.element.textContent = ""

    renderSkeleton(this.element, {
      bare: this.bare,
      fields: hintedFields(this.element)
    })

    fetchConfig(this.origin, this.token)
      .then((response) => {
        if (!response.ok || !response.body || !response.body.fields) throw new Error("no config")
        this.config = response.body
        this.draw()
      })
      .catch((error) => this.giveUp(error))
  }

  // The customer's visitor sees the hosted-page link they would have seen with
  // JavaScript switched off. Nothing English is invented here, because there is
  // nothing to invent it from -- every word this widget can say arrives in the
  // config we just failed to fetch. That is exactly why the fallback link lives
  // in the pasted markup rather than in a <noscript>: <noscript> answers only
  // "is JavaScript enabled?", which is the least likely of the ways this fails.
  giveUp(error) {
    this.element.textContent = ""
    this.fallback.forEach((node) => this.element.appendChild(node))
    this.element.removeAttribute("aria-busy")

    if (window.console && console.warn) {
      console.warn(`[messagekit] sign-up form ${this.token} could not load`, error)
    }
  }

  draw() {
    const config = this.config

    this.element.textContent = ""
    this.element.removeAttribute("aria-busy")
    this.element.setAttribute("role", "region")
    if (config.heading) this.element.setAttribute("aria-label", config.heading)

    // Brand data, set as a custom property rather than baked into the sheet, so
    // one stylesheet serves every customer and the button is never a MessageKit
    // colour on somebody else's website.
    if (config.accent) {
      this.element.style.setProperty("--mk-accent", config.accent)
      this.element.style.setProperty("--mk-accent-text", readableOn(config.accent))
    }

    this.refs = renderForm(config, { bare: this.bare, headingLevel: this.headingLevel })
    this.refs.form.addEventListener("submit", (event) => this.send(event))
    this.element.appendChild(this.refs.form)
  }

  values() {
    const values = { stamp: this.config.stamp }

    Object.keys(this.refs.inputs).forEach((name) => {
      values[name] = this.refs.inputs[name].value
    })
    if (this.refs.consent) values.consent = this.refs.consent.checked ? "1" : ""

    // Collected separately, and by CHECKEDNESS. A checkbox's .value is its
    // value attribute whether or not it is ticked, so a segment box read
    // through refs.inputs above would submit every segment on the form -- and
    // put people into groups they never asked for, on every embedded copy at
    // once. An Array under one key, expanded by api.js into repeated
    // `segments[]` params, which is the shape the hosted page posts too.
    if (this.refs.segments) {
      const chosen = []
      this.refs.segments.forEach((box) => {
        if (box.checked) chosen.push(box.value)
      })
      values["segments[]"] = chosen
    }

    // Always sent, always empty when a person filled the form in.
    const honeypot = this.refs.form.querySelector('[data-mk="honeypot"]')
    if (honeypot) values[this.config.honeypot] = honeypot.value

    return values
  }

  send(event) {
    event.preventDefault()
    if (this.sending) return

    const values = this.values()
    const problem = validate(this.config, values)
    if (problem) return showError(this.refs, problem)

    clearErrors(this.refs)
    this.busy(true)

    submit(this.config.submit_url, values)
      .then((response) => {
        if (response.status === 422 && response.body && response.body.messages) {
          this.busy(false)
          return showError(this.refs, {
            field: response.body.field,
            message: response.body.messages[0]
          })
        }

        if (!response.ok || !response.body || !response.body.ok) throw new Error("refused")

        this.finish(response.body)
      })
      .catch(() => {
        this.busy(false)
        // Every value they typed is still in the fields -- nothing is redrawn
        // on a failure, so a flaky network costs them nothing.
        showError(this.refs, { field: null, message: this.config.errors.network })
      })
  }

  busy(sending) {
    this.sending = sending
    this.refs.button.disabled = sending
    this.refs.form.setAttribute("aria-busy", sending ? "true" : "false")
  }

  finish(body) {
    const destination = body.redirect_url || this.config.redirect_url

    if (destination && isWebUrl(destination)) {
      // The current frame, not window.top: a page builder previews the site in
      // an iframe, and navigating the top window out of the editor would be a
      // memorable bug.
      window.location.assign(destination)
      return
    }

    const confirmation = renderConfirmation(this.config, body, {
      bare: this.bare,
      headingLevel: this.headingLevel
    })

    this.element.textContent = ""
    this.element.appendChild(confirmation)
    confirmation.focus()
  }
}

// The server validated this when it was saved and it never comes from the
// request, so this is the second of two locks rather than the only one.
function isWebUrl(value) {
  try {
    const url = new URL(value, window.location.href)
    return url.protocol === "http:" || url.protocol === "https:"
  } catch {
    return false
  }
}

function clampHeadingLevel(value) {
  const level = parseInt(value, 10)
  return level >= 1 && level <= 6 ? level : 2
}

function hintedFields(element) {
  const hint = element.getAttribute("data-mk-fields")
  return (hint ? hint.split(/\s+/).filter(Boolean) : ["email", "name"])
}

// Black or white on the brand's accent, by luminance. The same decision the
// application makes elsewhere, kept here rather than imported because nothing
// in this directory may reach outside it.
function readableOn(hex) {
  const value = hex.replace("#", "")
  if (value.length !== 6) return "#ffffff"

  const channels = [0, 2, 4].map((at) => {
    const channel = parseInt(value.slice(at, at + 2), 16) / 255
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
  })

  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
  return luminance > 0.45 ? "#000000" : "#ffffff"
}
